// TODO: (Later iteration) autofill & disable school fields if voucher got entered

import { Text, Button, Flex, Center, Link as ExternalLink, VStack } from '@chakra-ui/react';
import { ReactElement, useState } from 'react';
import { AuthType } from '@novaheal/types';
import Joi from 'joi';
import { AutocompleteField } from 'components/common/Form';
import { FieldError } from 'react-hook-form';
import { caughtErrorGlobally } from 'utils/axios';
import { AxiosError } from 'axios';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { useWelcomePostStep } from 'hooks/api/backend/welcome/useWelcomeStep';
import { NamePrefillData } from 'types/WelcomeScreen';
import { toastContent } from 'config/toast';
import { ErrorStatusType } from 'types/StatusTypes';
import { ExternalRoutes } from 'types/Routes';
import { useCountryApi } from 'hooks/api/backend/useCountryApi';
import { CloseIcon, LanguageIcon, UserIcon } from 'assets/icons/v2';
import { Form, RHFCheckboxField, RHFFormInput } from 'components/common/Form/v2';
import { NovaIcon } from 'components/ui/novaIcon/NovaIcon';
import { useUser } from 'utils/auth';
import { WelcomeScreenOptions } from './Welcome';

type NameValues = {
  firstName: string;
  lastName: string;
  countryId: string;
  country: string;
  marketingEmailOptIn?: boolean;
};

const getSchema = (authType?: AuthType): Joi.ObjectSchema => {
  const baseSchema = {
    firstName: Joi.string().required().messages({
      'string.empty': errorMessage.empty,
    }),
    lastName: Joi.string().required().messages({
      'string.empty': errorMessage.empty,
    }),
    countryId: Joi.string().required().messages({
      '*': 'Du musst ein Land aus der Liste auswählen',
      'any.required': 'Du musst ein Land aus der Liste auswählen',
      'string.empty': 'Du musst ein Land aus der Liste auswählen',
    }),
    country: Joi.string().required().messages({
      '*': 'Du musst ein Land aus der Liste auswählen',
      'any.required': 'Du musst ein Land aus der Liste auswählen',
      'string.empty': 'Du musst ein Land aus der Liste auswählen',
    }),
  };

  return Joi.object({
    ...baseSchema,
    ...(!!authType &&
      authType !== AuthType.LOCAL && {
        marketingEmailOptIn: Joi.boolean(),
      }),
  });
};

const getDefaultValues = (authType?: AuthType, prefillData?: NamePrefillData): NameValues => ({
  firstName: prefillData?.firstName || '',
  lastName: prefillData?.lastName || '',
  country: prefillData?.country || '',
  countryId: prefillData?.countryId || '',
  ...(!!authType &&
    authType !== AuthType.LOCAL && {
      marketingEmailOptIn: prefillData?.marketingEmailOptIn ?? false,
    }),
});

export const UserInfo = (props: WelcomeScreenOptions): ReactElement => {
  const { onContinue, stepData } = props;
  const { data: user } = useUser();
  const prefillData = stepData.currentData.prefillData as NamePrefillData;
  const toast = useNovaToast();
  const [isLoading, setLoading] = useState(false);
  const { mutateAsync: updateWelcomeStep } = useWelcomePostStep();
  const { getAutoCompleteItems } = useCountryApi();

  const handleSubmit = async (values: NameValues): Promise<void> => {
    setLoading(true);
    try {
      const onboardingData = await updateWelcomeStep({
        stepIdentifier: stepData.currentStepIdentifier,
        totalSteps: stepData.totalSteps,
        step: stepData.currentStep,
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          countryId: values.countryId,
          ...(!!user?.authType &&
            user?.authType !== AuthType.LOCAL && {
              marketingEmailOptIn: values.marketingEmailOptIn,
            }),
        },
      });
      onContinue(onboardingData);
    } catch (error) {
      if (caughtErrorGlobally(error as AxiosError)) {
        toast.show(toastContent.error[ErrorStatusType.SERVER_ERROR]);
        return;
      }
      toast.show({
        variant: ToastVariant.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex width="100%" direction="column" gap="0">
      <Text as="h1" textStyle="h2.medium" color="text.primary" mb="25px">
        Wer bist du?
      </Text>
      <Text textStyle="body.m.regular" color="text.secondary" mb="25px">
        Gib uns noch ein paar Infos über dich.
      </Text>

      <Form<NameValues, ReturnType<typeof getSchema>>
        onSubmit={handleSubmit}
        schema={getSchema(user?.authType)}
        autoComplete="off"
        options={{
          defaultValues: getDefaultValues(user?.authType, prefillData),
        }}
      >
        {({ register, formState, setValue, getValues, watch, resetField }) => {
          return (
            <>
              <Flex direction="column" gap="l">
                <RHFFormInput
                  placeholder="Vorname"
                  name="firstName"
                  defaultValue={prefillData.firstName}
                  leftElement={
                    <NovaIcon icon={UserIcon} size="s" aria-hidden color="icon.tertiary" />
                  }
                />
                <RHFFormInput
                  name="lastName"
                  placeholder="Nachname"
                  defaultValue={prefillData.lastName}
                  leftElement={
                    <NovaIcon icon={UserIcon} size="s" aria-hidden color="icon.tertiary" />
                  }
                />

                <AutocompleteField
                  getAutocompleteItems={getAutoCompleteItems}
                  initialName={prefillData.country}
                  initialId={prefillData.countryId}
                  registrationName="country"
                  error={formState.errors.countryId as FieldError}
                  idName="countryId"
                  disableCreate
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  placeholder="Land oder Region"
                  leftElement={
                    <LanguageIcon
                      color={watch('country') ? 'icon.accent' : 'icon.quaternary'}
                      boxSize="18px"
                    />
                  }
                  zIndex={1}
                  rightElement={
                    watch('country') ? (
                      <Button
                        ml="m"
                        bg="transparent"
                        _hover={{ backgroundColor: 'none' }}
                        _active={{ backgroundColor: 'none' }}
                        _focus={{ outline: 'none' }}
                        onClick={() => {
                          resetField('country');
                          resetField('countryId');
                        }}
                      >
                        <Center bg="text.quaternary" rounded="full" boxSize="16px">
                          <CloseIcon color="icon.onAccent" mt="3px" w="10px" m={0} boxSize="18px" />
                        </Center>
                      </Button>
                    ) : undefined
                  }
                />
              </Flex>

              {user?.authType !== undefined && user?.authType !== AuthType.LOCAL && (
                <VStack align="left" pt="5">
                  <RHFCheckboxField<NameValues>
                    name="marketingEmailOptIn"
                    labelElement={
                      <Text textStyle="body.s.regular">
                        Ja, ich möchte von NOVAHEAL per E-Mail über Neuigkeiten, Angebote,
                        Produktupdates und Feedbackanfragen informiert werden. Die Einwilligung kann
                        ich jederzeit in meinen Profileinstellungen oder per E-Mail widerrufen.
                        Weitere Informationen in unserer{' '}
                        <ExternalLink
                          color="text.info"
                          href={ExternalRoutes.PRIVACY}
                          target="_blank"
                          textStyle="body.s.regular"
                          rel="noopener noreferrer"
                        >
                          Datenschutzerklärung
                        </ExternalLink>
                        .
                      </Text>
                    }
                  />
                </VStack>
              )}

              <Button
                translate="no"
                variant="primary"
                type="submit"
                loading={isLoading}
                loadingText="Speichern"
                mt="10px"
                w="full"
                py="10px"
              >
                Weiter
              </Button>
            </>
          );
        }}
      </Form>
    </Flex>
  );
};
